// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { ContainerType, ContentLinkType } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { createIdentitySet } from "../../../utilities/identity";
import { PlanExtensionKeys, PlanViewExtensionKeys } from "../../../service/planner/extensions/IPlanExtensionsResource";
import { normalizeContainerType } from "../../../utilities/PlanUtilities";
import { Calendar } from "../calendar/Calendar";
import { CategoryDefinition } from "../categoryDefinition/CategoryDefinition";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
import { ContentLink } from "../contentLink/ContentLink";
import { PlanContextLink } from "../planContextLink/PlanContextLink";
// Utilities
import { getDiff } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { Temporal } from "temporal-polyfill-lite";
/**
 * The plan entity implementation.
 */ export class Plan extends PatchableAndDeltaSyncableODataEntity {
    getReferenceLinks() {
        return this.links.filter((link)=>link.linkType === ContentLinkType.DocumentReference || link.linkType === ContentLinkType.WebReference).map((link)=>link);
    }
    getPropertyMappingOverride() {
        return {
            links: "links@delta",
            contexts: "contexts@delta"
        };
    }
    mapEntityChangesToResource(changed, source) {
        const resourceKeyMap = this.getChangePropertyMapping();
        return {
            [resourceKeyMap.displayName]: changed.displayName,
            [resourceKeyMap.extensions]: changed.extensions && this.serializeExtensions(),
            [resourceKeyMap.links]: this.processSubEntityArray(this.links, source.links),
            [resourceKeyMap.contexts]: this.processSubEntityArray(this.contexts, source.contexts),
            [resourceKeyMap.planView]: this.serializePlanViewForUpdate(changed.planView, source.planView),
            [resourceKeyMap.sharedWithContainers]: changed.sharedWithContainers,
            [resourceKeyMap.contentSensitivityLabelAssignment]: this.normalizeContentSensitivityLabelAssignment(changed.contentSensitivityLabelAssignment, source),
            [resourceKeyMap.calendar]: this.serializeCalendar(changed)
        };
    }
    serializeCalendar(changed) {
        // [TODO:3044338] Gated on EnableEvoCalendar so we never include calendar until the service contract is rolled out.
        if (AppContext?.appConfiguration?.flights?.EnableEvoCalendar !== true) {
            return undefined;
        }
        if (changed.calendar === undefined || this.calendar == null) {
            return undefined;
        }
        return this.calendar.forSerialization();
    }
    /**
     * Normalizes the contentSensitivityLabelAssignment for resource mapping.
     * If sensitivityLabelId changed, we must include both sensitivityLabelId AND assignmentMethod for atomic updates (API requirement).
     * This handles: adding a label (null → obj), removing a label (obj → null), or changing label ID (obj1 → obj2).
     */ normalizeContentSensitivityLabelAssignment(changed, source) {
        if (changed === undefined) {
            return undefined;
        }
        // If the label ID changed (add, remove, or change), ensure we include the current full assignment object or null
        if (this.contentSensitivityLabelAssignment?.sensitivityLabelId !== source.contentSensitivityLabelAssignment?.sensitivityLabelId) {
            return this.contentSensitivityLabelAssignment;
        }
        return changed;
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            archivalInfo: (deltaValue)=>({
                    isArchived: deltaValue.isArchived,
                    justification: deltaValue.justification,
                    modifiedBy: deltaValue.modifiedBy,
                    modifiedDate: deltaValue.modifiedDate ? Temporal.PlainDate.from(deltaValue.modifiedDate) : null
                }),
            container: (deltaValue)=>({
                    containerType: normalizeContainerType(deltaValue.containerType),
                    containerId: deltaValue.containerId
                }),
            contexts: (deltaValue)=>{
                const updatedContexts = this.applySubEntityArrayDelta(deltaValue, this.contexts);
                return updatedContexts;
            },
            creationInfo: (deltaValue)=>({
                    ...deltaValue,
                    createdDateTime: Temporal.Instant.from(deltaValue.createdDateTime)
                }),
            extensions: (deltaValue)=>Plan.deserializeExtensions(deltaValue),
            lastModifiedDateTime: (deltaValue)=>Temporal.Instant.from(deltaValue),
            links: (deltaValue)=>{
                const updatedLinks = this.applySubEntityArrayDelta(deltaValue, this.links);
                return updatedLinks;
            },
            personalView: (deltaValue)=>Plan.deserializePersonalView(deltaValue.extensions || {}),
            planView: (deltaValue)=>this.deserializePlanViewFromDelta(deltaValue),
            sharedWithContainers: (deltaValue)=>deltaValue.map((container)=>({
                        containerType: normalizeContainerType(container.containerType),
                        containerId: container.containerId,
                        accessLevel: container.accessLevel
                    })),
            calendar: (deltaValue)=>Plan.tryDeserializeCalendar(deltaValue)
        });
    }
    deserializePlanViewFromDelta(delta) {
        const output = {};
        // Handle categories delta if present (using the @delta field)
        const categoriesDelta = delta["categories@delta"];
        if (categoriesDelta) {
            const updatedCategories = this.applySubEntityArrayDelta(categoriesDelta, this.planView.categories);
            if (updatedCategories) {
                output.categories = updatedCategories;
            }
        }
        // Handle extensions delta if present
        if (delta.extensions) {
            // Cast to Record<string, string> for deserialization since partial extensions still follow the same structure
            const extensions = delta.extensions;
            // Start with all Evo extensions as the base (superset)
            const evoData = Plan.deserializePlanViewEvoExtensions(extensions);
            // Override with legacy data if present (takes precedence for overlapping fields)
            const legacyData = Plan.deserializePlanViewLegacyOverrides(extensions);
            // Merge visual data with legacy taking precedence for overlapping fields
            const finalVisuals = Plan.mergePlanViewVisuals(evoData.visuals, legacyData.visuals);
            // Update output with merged data
            if (finalVisuals) {
                output.visuals = finalVisuals;
            }
            // Merge any other extensions (excluding visuals which we handled above)
            const { visuals: evoVisuals, ...restEvoData } = evoData;
            const { visuals: legacyVisuals, ...restLegacyData } = legacyData;
            Object.assign(output, restEvoData, restLegacyData);
        }
        return output;
    }
    static fromResource(resource) {
        return new Plan({
            id: resource.id,
            version: resource["@etag"],
            archivalInfo: {
                isArchived: resource.archivalInfo.isArchived,
                justification: resource.archivalInfo.justification,
                modifiedBy: resource.archivalInfo.modifiedBy ? createIdentitySet(resource.archivalInfo.modifiedBy) : null,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            displayName: resource.displayName,
            capabilities: resource.capabilities,
            container: {
                containerType: normalizeContainerType(resource.container.containerType),
                containerId: resource.container.containerId
            },
            sharedWithContainers: resource.sharedWithContainers.map((container)=>{
                return {
                    containerType: normalizeContainerType(container.containerType),
                    containerId: container.containerId,
                    accessLevel: container.accessLevel
                };
            }),
            creationInfo: {
                ...resource.creationInfo,
                createdBy: createIdentitySet(resource.creationInfo.createdBy),
                createdDateTime: Temporal.Instant.from(resource.creationInfo.createdDateTime)
            },
            permissionSetId: resource.permissionSetId,
            personalView: resource.personalView?.extensions ? this.deserializePersonalView(resource.personalView.extensions) : {},
            planView: this.deserializePlanView(resource.planView, resource.id),
            extensions: this.deserializeExtensions(resource.extensions),
            lastModifiedBy: createIdentitySet(resource.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            links: resource.links.map((link)=>ContentLink.fromResource(link)),
            contexts: resource.contexts.map((context)=>PlanContextLink.fromResource(context)),
            contentSensitivityLabelAssignment: resource.contentSensitivityLabelAssignment ?? null,
            calendar: Plan.tryDeserializeCalendar(resource.calendar)
        });
    }
    static fromPersistedResource(resource) {
        return new Plan({
            id: resource.id,
            version: resource["@etag"],
            displayName: resource.displayName,
            archivalInfo: {
                isArchived: resource.archivalInfo.isArchived,
                justification: resource.archivalInfo.justification,
                modifiedBy: resource.archivalInfo.modifiedBy ? createIdentitySet(resource.archivalInfo.modifiedBy) : null,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            capabilities: resource.capabilities,
            container: {
                containerType: normalizeContainerType(resource.container.containerType),
                containerId: resource.container.containerId
            },
            sharedWithContainers: resource.sharedWithContainers.map((container)=>{
                return {
                    containerType: normalizeContainerType(container.containerType),
                    containerId: container.containerId,
                    accessLevel: container.accessLevel
                };
            }),
            creationInfo: {
                ...resource.creationInfo,
                createdBy: createIdentitySet(resource.creationInfo.createdBy),
                createdDateTime: Temporal.Instant.from(resource.creationInfo.createdDateTime)
            },
            permissionSetId: resource.permissionSetId,
            personalView: resource.personalView?.extensions ? this.deserializePersonalView(resource.personalView.extensions) : {},
            planView: this.deserializePlanView(resource.planView, resource.id),
            extensions: this.deserializeExtensions(resource.extensions),
            lastModifiedBy: createIdentitySet(resource.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            links: resource.links.map((link)=>ContentLink.fromResource(link)),
            contexts: resource.contexts.map((context)=>PlanContextLink.fromResource(context)),
            contentSensitivityLabelAssignment: resource.contentSensitivityLabelAssignment ?? null,
            calendar: Plan.tryDeserializeCalendar(resource.calendar)
        });
    }
    static tryDeserializeCalendar(calendarResource) {
        if (!calendarResource) {
            return undefined;
        }
        try {
            return Calendar.fromResource(calendarResource);
        } catch  {
            return undefined;
        }
    }
    /**
     * Deserializes the extensions of the plan.
     * The values must be serializable to JSON.
     */ static deserializeExtensions(extensions) {
        const output = {};
        // First parse the legacy data if it exists
        const serializedLegacyData = extensions[PlanExtensionKeys.legacyData];
        if (serializedLegacyData) {
        // Do nothing here as we do not have any legacy fields other than iCalendar and we are cutting that
        }
        // Handle specific known extension keys
        for(const key in PlanExtensionKeys){
            const extensionKey = PlanExtensionKeys[key];
            // Skip legacyData as we handle it separately
            if (extensionKey === PlanExtensionKeys.legacyData) {
                continue;
            }
            const serializedData = extensions[extensionKey];
            if (serializedData !== undefined) {
                try {
                    // Always try to parse as JSON since we always stringify during serialization
                    output[extensionKey] = JSON.parse(serializedData);
                } catch (parseError) {
                    throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=${extensionKey}]`);
                }
            }
        }
        return output;
    }
    /**
     * Deserializes the personal view of the plan.
     * The values must be serializable to JSON.
     */ static deserializePersonalView(extensions) {
        // Start with all Evo extensions as the base (superset)
        const evoData = this.deserializePersonalViewEvoExtensions(extensions);
        // Since there are no legacy personal view fields for a plan, we can return the Evo data directly
        return evoData;
    }
    /**
     * Deserializes all Evo extensions automatically for personal view.
     * This method will handle any future extensions that are added to the Evo format.
     */ static deserializePersonalViewEvoExtensions(extensions) {
        const evoData = {};
        // Process all Evo extension keys (currently none defined for plan personal view)
        // This pattern is ready for future extensions
        return evoData;
    }
    /**
     * Deserializes the plan view of the plan.
     * The values must be serializable to JSON.
     */ static deserializePlanView(planView, planId) {
        const categoryResources = planView.categories;
        const output = {
            categories: categoryResources.map((category)=>CategoryDefinition.fromResource(category)).sort((a, b)=>a.id.localeCompare(b.id)),
            visuals: {
                background: {
                    color: null,
                    overlayImage: null
                }
            }
        };
        const extensions = planView.extensions;
        // Start with all Evo extensions as the base (superset)
        const evoData = this.deserializePlanViewEvoExtensions(extensions);
        // Override with legacy data if present (takes precedence for overlapping fields)
        const legacyData = this.deserializePlanViewLegacyOverrides(extensions);
        // Merge visual data with legacy taking precedence for overlapping fields
        const finalVisuals = this.mergePlanViewVisuals(evoData.visuals, legacyData.visuals);
        // Update output with merged data
        if (finalVisuals) {
            output.visuals = finalVisuals;
        }
        // Merge any other extensions
        Object.assign(output, evoData, legacyData);
        return output;
    }
    /**
     * Deserializes all Evo extensions automatically for plan view.
     * This method will handle any future extensions that are added to the Evo format.
     */ static deserializePlanViewEvoExtensions(extensions) {
        const evoData = {};
        if (extensions) {
            // Process all Evo extension keys
            for(const key in PlanViewExtensionKeys){
                const extensionKey = PlanViewExtensionKeys[key];
                // Skip legacy data - we handle that separately
                if (extensionKey === PlanViewExtensionKeys.legacyData) {
                    continue;
                }
                const serializedData = extensions[extensionKey];
                if (serializedData !== undefined) {
                    try {
                        if (extensionKey === PlanViewExtensionKeys.visuals) {
                            const visualData = JSON.parse(serializedData);
                            evoData.visuals = {
                                background: {
                                    color: visualData.background?.color ?? null,
                                    overlayImage: visualData.background?.overlayImage ?? null
                                }
                            };
                        } else if (extensionKey === PlanViewExtensionKeys.conditionalColoringRules) {
                            const parsedData = JSON.parse(serializedData);
                            const rulesData = parsedData.rules ?? [];
                            evoData.conditionalColoringRules = rulesData.map((rule)=>({
                                    id: rule.id ?? "",
                                    columnId: rule.columnId ?? "",
                                    colorIndex: rule.colorIndex ?? 0,
                                    expression: {
                                        operator: rule.expression?.operator ?? "",
                                        firstValue: rule.expression?.firstValue ?? "",
                                        secondValue: rule.expression?.secondValue
                                    },
                                    order: rule.order ?? 0
                                }));
                        } else {
                            // Always try to parse as JSON since we always stringify during serialization
                            evoData[extensionKey] = JSON.parse(serializedData);
                        }
                    } catch (parseError) {
                        throw new Error(`Deserialization Error: [Entity=${this.name}][Field=PlanViewExtensions][Key=${extensionKey}]`);
                    }
                }
            }
        }
        return evoData;
    }
    /**
     * Deserializes legacy extensions that override Evo data for overlapping fields.
     * This method only handles the known legacy format fields for plan view.
     */ static deserializePlanViewLegacyOverrides(extensions) {
        const legacyOverrides = {};
        if (extensions) {
            const serializedLegacyData = extensions[PlanViewExtensionKeys.legacyData];
            if (serializedLegacyData) {
                try {
                    const legacyExtensionData = JSON.parse(serializedLegacyData);
                    // Legacy controls the visual background properties
                    legacyOverrides.visuals = {
                        background: {
                            color: legacyExtensionData.backgroundBaseColor ?? null,
                            overlayImage: legacyExtensionData.backgroundOverlayImage ?? null
                        }
                    };
                } catch (parseError) {
                    throw new Error(`Deserialization Error: [Entity=${this.name}][Field=PlanViewExtensions][Key=legacyData]`);
                }
            }
        }
        return legacyOverrides;
    }
    /**
     * Merges Evo and legacy visual data.
     * Legacy takes precedence for overlapping fields, but Evo fields that don't exist in legacy are preserved.
     */ static mergePlanViewVisuals(evoVisuals, legacyVisuals) {
        // If no legacy override, use Evo (or undefined if neither exists)
        if (!legacyVisuals) {
            return evoVisuals;
        }
        // If no Evo data, use legacy
        if (!evoVisuals) {
            return legacyVisuals;
        }
        // Start with Evo data as base, then override with legacy values for specific fields
        const mergedVisuals = {
            background: {
                color: legacyVisuals.background.color ?? evoVisuals.background.color,
                overlayImage: legacyVisuals.background.overlayImage ?? evoVisuals.background.overlayImage
            }
        };
        return mergedVisuals;
    }
    getClone() {
        return new Plan({
            id: this.id,
            version: this.version,
            displayName: this.displayName,
            archivalInfo: cloneDeep(this.archivalInfo),
            capabilities: cloneDeep(this.capabilities),
            container: cloneDeep(this.container),
            creationInfo: cloneDeep(this.creationInfo),
            permissionSetId: this.permissionSetId,
            personalView: cloneDeep(this.personalView),
            planView: {
                categories: this.planView.categories.map((category)=>category.getClone()),
                visuals: cloneDeep(this.planView.visuals)
            },
            extensions: cloneDeep(this.extensions),
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime),
            sharedWithContainers: cloneDeep(this.sharedWithContainers),
            links: this.links.map((link)=>link.getClone()),
            contexts: this.contexts.map((context)=>context.getClone()),
            contentSensitivityLabelAssignment: cloneDeep(this.contentSensitivityLabelAssignment),
            calendar: this.calendar ? this.calendar.getClone() : null
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            displayName: this.displayName,
            container: {
                containerId: this.container.containerId,
                containerType: this.container.containerType
            },
            creationInfo: {
                ...this.creationInfo,
                createdDateTime: this.creationInfo.createdDateTime.toString()
            },
            personalView: {
                extensions: {}
            },
            planView: this.serializePlanViewForPersistence(),
            extensions: this.serializeExtensions(),
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            links: this.links.map((link)=>link.forSerialization()),
            contexts: this.contexts.map((context)=>context.forSerialization()),
            archivalInfo: {
                isArchived: this.archivalInfo.isArchived,
                justification: this.archivalInfo.justification,
                modifiedBy: this.archivalInfo.modifiedBy,
                modifiedDate: this.archivalInfo.modifiedDate?.toString() ?? null
            },
            permissionSetId: this.permissionSetId,
            capabilities: this.capabilities,
            sharedWithContainers: this.sharedWithContainers.map((container)=>({
                    containerId: container.containerId,
                    containerType: container.containerType,
                    accessLevel: container.accessLevel
                })),
            contentSensitivityLabelAssignment: this.contentSensitivityLabelAssignment ?? null,
            calendar: this.calendar?.forSerialization()
        };
    }
    forCreate() {
        return {
            displayName: this.displayName,
            container: {
                containerId: this.container.containerId,
                containerType: this.container.containerType
            },
            extensions: this.serializeExtensions(),
            links: this.links.map((link)=>link.forCreate()),
            contexts: this.contexts.map((context)=>context.forCreate()),
            planView: this.serializePlanView(),
            contentSensitivityLabelAssignment: this.contentSensitivityLabelAssignment ?? undefined,
            // [TODO:3044338] Gated on EnableEvoCalendar so we never include calendar in create payloads until the service contract is rolled out.
            calendar: AppContext?.appConfiguration?.flights?.EnableEvoCalendar === true ? this.calendar?.forCreate() : undefined
        };
    }
    /**
     * Serializes the extensions of the plan.
     * The values must be serializable to JSON.
     */ serializeExtensions() {
        const output = {};
        // Always serialize to both Legacy and Evo formats to support round-trip deserialization across v3 and v4 apis
        const legacyData = {};
        output[PlanExtensionKeys.legacyData] = JSON.stringify(legacyData);
        // Serialize to Evo format - serialize all extension keys except legacyData
        for(const key in PlanExtensionKeys){
            const extensionKey = PlanExtensionKeys[key];
            // Skip legacyData as we handle it separately
            if (extensionKey === PlanExtensionKeys.legacyData) {
                continue;
            }
            const value = this.extensions[extensionKey];
            if (value !== undefined) {
                // Always JSON stringify - the server expects string values in the extensions record
                output[extensionKey] = JSON.stringify(value);
            }
        }
        return output;
    }
    serializePlanViewForPersistence() {
        const categories = this.planView.categories.map((category)=>category.forSerialization());
        return {
            categories: categories,
            extensions: this.serializePlanViewExtensions()
        };
    }
    /**
     * Serializes the plan view of the plan.
     * The values must be serializable to JSON.
     */ serializePlanView() {
        const extensions = this.serializePlanViewExtensions();
        return {
            extensions
        };
    }
    /**
     * Serializes the plan view of the plan.
     * The values must be serializable to JSON.
     */ serializePlanViewExtensions() {
        const extensions = {};
        // Always serialize to both Legacy and Evo formats to support round-trip deserialization as edits can happen from either v3 or v4 api.
        // This ensures that regardless of how the plan is later deserialized, the data is preserved
        // Serialize to Legacy format
        const legacyData = {
            backgroundBaseColor: this.planView.visuals.background.color ?? null,
            backgroundOverlayImage: this.planView.visuals.background.overlayImage ?? null
        };
        extensions[PlanViewExtensionKeys.legacyData] = JSON.stringify(legacyData);
        // Serialize to Evo format
        extensions[PlanViewExtensionKeys.visuals] = JSON.stringify({
            background: {
                color: this.planView.visuals.background.color ?? null,
                overlayImage: this.planView.visuals.background.overlayImage ?? null
            }
        });
        // Serialize conditional coloring rules if present (empty array is valid)
        if (this.planView.conditionalColoringRules !== undefined) {
            extensions[PlanViewExtensionKeys.conditionalColoringRules] = JSON.stringify({
                rules: this.planView.conditionalColoringRules
            });
        }
        return extensions;
    }
    /**
     * Serializes the plan view for update.
     * Only serializes visual extensions if visuals have actually changed at the value level.
     * @param changedPlanView The changed plan view (from getDiff).
     * @param sourcePlanView The source plan view for comparison.
     */ serializePlanViewForUpdate(changedPlanView, sourcePlanView) {
        if (!changedPlanView) {
            return undefined;
        }
        const { categories, visuals, conditionalColoringRules } = changedPlanView;
        // Early return if nothing changed
        if (!categories && !visuals && conditionalColoringRules === undefined) {
            return undefined;
        }
        const result = {};
        // Handle categories changes
        if (categories && sourcePlanView) {
            result["categories@delta"] = this.processSubEntityArray(this.planView.categories, sourcePlanView.categories);
        }
        // Handle visuals/conditionalColoringRules changes - serialize all extensions together
        // since they share a single extensions bag on the service
        if (sourcePlanView) {
            let extensionsChanged = false;
            if (visuals) {
                const visualsDiff = getDiff(sourcePlanView.visuals, this.planView.visuals, undefined, true);
                extensionsChanged = !isEmpty(visualsDiff);
            }
            if (conditionalColoringRules !== undefined) {
                const conditionalColoringDiff = getDiff(sourcePlanView.conditionalColoringRules, this.planView.conditionalColoringRules, undefined, true);
                if (!isEmpty(conditionalColoringDiff)) {
                    extensionsChanged = true;
                }
            }
            if (extensionsChanged) {
                result.extensions = this.serializePlanViewExtensions();
            }
        }
        // Return undefined if no actual changes were added to result
        return Object.keys(result).length > 0 ? result : undefined;
    }
    getPlanType() {
        if (this.container.containerType === ContainerType.Project) {
            return PlanType.Premium;
        } else if (this.container.containerType === ContainerType.ToDo) {
            return PlanType.ToDo;
        } else {
            return PlanType.Unified;
        }
    }
    constructor(propertyBag){
        super(propertyBag);
        this.archivalInfo = {
            isArchived: propertyBag.archivalInfo?.isArchived ?? false,
            justification: propertyBag.archivalInfo?.justification ?? null,
            modifiedBy: propertyBag.archivalInfo?.modifiedBy ?? null,
            modifiedDate: propertyBag.archivalInfo?.modifiedDate ? Temporal.PlainDate.from(propertyBag.archivalInfo.modifiedDate) : null
        };
        this.displayName = propertyBag.displayName;
        this.capabilities = propertyBag.capabilities ?? {
            entityExtensions: false
        };
        this.container = propertyBag.container;
        this.creationInfo = propertyBag.creationInfo ?? {
            createdBy: propertyBag.lastModifiedBy,
            createdDateTime: Temporal.Now.instant(),
            externalId: null,
            externalVersion: null,
            scenarioId: null,
            scope: null,
            externalUrl: null
        };
        this.permissionSetId = propertyBag.permissionSetId ?? "";
        this.personalView = propertyBag.personalView ?? {};
        this.planView = propertyBag.planView ?? {
            categories: [],
            visuals: {
                background: {
                    color: null,
                    overlayImage: null
                }
            }
        };
        this.extensions = propertyBag.extensions ?? {
            copilot: {}
        };
        this.lastModifiedBy = propertyBag.lastModifiedBy;
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime ?? Temporal.Now.instant();
        this.sharedWithContainers = propertyBag.sharedWithContainers ?? [];
        this.links = propertyBag.links ?? [];
        this.contexts = propertyBag.contexts ?? [];
        this.contentSensitivityLabelAssignment = propertyBag.contentSensitivityLabelAssignment ?? null;
        this.calendar = propertyBag.calendar ?? null;
    }
}
Plan.NameMaxLength = 255;
